// File: src/components/Staff/ClockInOutPanel.jsx

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchEmergencyStatus, isFeatureLocked } from '@/utils/emergencyLock';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { useNavigate } from 'react-router-dom';

const ClockInOutPanel = () => {
  const [status, setStatus] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const { canClockInOut, user } = useUserPermissions();
  const navigate = useNavigate();

  useEffect(() => {
    if (!canClockInOut || !user?.staff_id) {
      navigate('/unauthorized');
    }
  }, [canClockInOut, user, navigate]);

  useEffect(() => {
    const checkStatus = async () => {
      const mode = await fetchEmergencyStatus();
      setIsLocked(isFeatureLocked(mode, ['cyberattack', 'offline']));
    };
    checkStatus();
  }, []);

  const getLocationAndSend = async (action) => {
    if (!navigator.geolocation) {
      setMessage('Geolocation not supported on this device.');
      return;
    }

    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          const endpoint = `/solo-mdsh-api/secure-clock/${action}`;
          const res = await axios.post(endpoint, {
            latitude,
            longitude,
            staff_id: user.staff_id,
          });

          setStatus(action);
          setMessage(res.data.message || `${action} recorded.`);
        } catch (err) {
          console.error('Clock error:', err);
          setMessage('❌ Error recording clock action.');
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        console.error('Geolocation error:', err);
        setMessage('⚠️ Unable to get your current location.');
        setLoading(false);
      }
    );
  };

  return (
    <div className="bg-neutral-800 p-6 rounded-2xl shadow-xl text-white max-w-md mx-auto">
      <h2 className="text-xl font-semibold mb-4">⏱️ Clock In / Out</h2>

      {isLocked ? (
        <div className="bg-red-600 text-white p-3 rounded-lg font-medium text-center">
          🚫 Clocking actions are disabled due to Emergency Mode.
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <button
            onClick={() => getLocationAndSend('in')}
            className="bg-green-600 hover:bg-green-700 p-2 px-4 rounded-xl font-semibold disabled:opacity-50"
            disabled={loading}
          >
            {loading && status === 'in' ? 'Clocking In...' : 'Clock In'}
          </button>
          <button
            onClick={() => getLocationAndSend('out')}
            className="bg-red-600 hover:bg-red-700 p-2 px-4 rounded-xl font-semibold disabled:opacity-50"
            disabled={loading}
          >
            {loading && status === 'out' ? 'Clocking Out...' : 'Clock Out'}
          </button>
        </div>
      )}

      {message && (
        <div className="mt-4 text-yellow-400 font-medium text-center">{message}</div>
      )}
    </div>
  );
};

export default ClockInOutPanel;
